import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import FilteredTableCore from "../../components/Organismos/Table/FilteredTableCore";
import {
  CUSTOM_FILTER_CHIP_ID,
  type IFilterData,
  type IHeader,
  type PanelFilterResetInfo,
} from "../../components/Organismos/Table/FilteredTable.types";
import {
  FIELDS_TO_IGNORE,
  getExcelColumnTitle,
} from "../../components/Organismos/Table/FilteredTable.utils";
import DropDownMultiple, {
  type DropDownOptionBase,
} from "../../components/staff/DropDownMultiple";
import Button from "../../components/staff/Button";
import DropdownButton from "../../components/Atomos/Buttons/DropdownButton";
import Gear from "../../components/Atomos/Icons/Gear";
import Log from "../../components/Atomos/Icons/Log";
import Play from "../../components/Atomos/Icons/Play";
import IIntegratorFirm from "../../interfaces/models/IIntegratorFirm";
import IIntegratorRunLog from "../../interfaces/models/IIntegratorRunLog";
import { getIntegratorFirms } from "../../Services/IntegratorFirm";
import {
  getLatestIntegratorRunLogs,
  runIntegratorBatch,
  runIntegratorImport,
} from "../../Services/IntegratorRunLog";
import { defaultPageSize } from "../../utils/const";
import { formatRegionDisplay } from "../../utils/formatRegionDisplay";
import { toastError, toastInfo, toastSuccess } from "../../utils/toast";
import GeneralSettingsModal from "./GeneralSettingsModal";
import LastRunDateCell from "./LastRunDateCell";
import UploadFeedModal, { type UploadFeedPayload } from "./UploadFeedModal";

/** A firm is "configured" when it has an IntegratorFirm document, whatever its `enabled` flag says. */
const SHOW_FIRMS_FILTER_NAME = "Show Firms";
const SHOW_FIRMS_DEFAULT = "all";
const SHOW_FIRMS_OPTIONS: DropDownOptionBase[] = [
  { id: "configured", name: "Configured" },
  { id: "not-configured", name: "Not configured" },
  { id: "all", name: "All" },
];

// Sentinel for IN/NOT_IN when no firm is configured: avoids `id IN ()` (invalid SQL in Cosmos).
const NO_CONFIGURED_FIRM = "__no_configured_firm__";

/** Shown where the design asks for a value the model does not hold yet. */
const NO_VALUE = "—";

const ACTION_BUTTON_CLASS =
  "!w-auto shrink-0 whitespace-nowrap !px-3 !py-1.5 text-sm";

/** The Run control never changes its label, so why it is disabled is told by the tooltip. */
function runButtonTooltip(
  hasIntegratorConfig: boolean,
  runningFirmId: string | null,
  firmId: string,
  isBatchRunning: boolean
): string {
  if (runningFirmId === firmId) {
    return "This firm's import is running…";
  }
  if (!hasIntegratorConfig) {
    return "Configure this firm's feed before running it";
  }
  if (isBatchRunning) {
    return "A batch is being queued";
  }
  if (runningFirmId) {
    return "Another firm's import is running";
  }
  return "Run Integrator Engine for this Firm";
}

function formatQueuedDate(queuedDate: number | undefined): string {
  if (!queuedDate) {
    return NO_VALUE;
  }
  return new Date(queuedDate).toLocaleDateString("en-US");
}

function firstNonBlankText(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value !== "string") {
      continue;
    }
    const text = value.trim();
    if (text) {
      return text;
    }
  }
  return "";
}

export default function IntegratorEngine(): ReactElement {
  const navigate = useNavigate();
  const endpoint = "/firm";

  // Per-firm Integrator configuration, fetched whole: the firm list comes from `/firm`, so
  // URL and Enabled are joined client-side (see ADR 0001).
  const [configs, setConfigs] = useState<IIntegratorFirm[] | null>(null);
  const [latestByFirmId, setLatestByFirmId] = useState<
    Map<string, IIntegratorRunLog>
  >(new Map());
  const [showFirms, setShowFirms] = useState<string>(SHOW_FIRMS_DEFAULT);

  // Firm whose run is in flight. Only one at a time from this screen: the backend rejects
  // a second run of the same firm with a 409, and one at a time keeps it obvious which
  // firm a toast belongs to.
  const [runningFirmId, setRunningFirmId] = useState<string | null>(null);
  const [isBatchRunning, setIsBatchRunning] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [uploadFirmId, setUploadFirmId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const list = await getIntegratorFirms();
      if (cancelled) {
        return;
      }
      const nextConfigs = list ?? [];
      setConfigs(nextConfigs);

      const firmIds = nextConfigs.map((config) => config.firmId);
      if (firmIds.length === 0) {
        setLatestByFirmId(new Map());
        return;
      }
      try {
        const logs = await getLatestIntegratorRunLogs(firmIds);
        if (!cancelled) {
          setLatestByFirmId(new Map(logs.map((log) => [log.firmId, log])));
        }
      } catch (e: any) {
        if (!cancelled) {
          setLatestByFirmId(new Map());
          toastError(e?.message ?? "Could not load execution logs.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const configByFirmId = useMemo(
    () => new Map((configs ?? []).map((config) => [config.firmId, config])),
    [configs]
  );

  const configuredFirmIds = useMemo(
    () => (configs ?? []).map((config) => config.firmId),
    [configs]
  );

  const persistentFilter = useMemo(() => {
    if (showFirms === "all") {
      return undefined;
    }
    // Until the configurations are known no firm can be classified, so filter everything
    // out instead of showing a list that contradicts the selected option.
    if (configs === null) {
      return { id: { value: [NO_CONFIGURED_FIRM], comparisonType: "IN" } };
    }
    const ids = configuredFirmIds.length ? configuredFirmIds : [NO_CONFIGURED_FIRM];
    return {
      id: {
        value: ids,
        comparisonType: showFirms === "configured" ? "IN" : "NOT_IN",
      },
    };
  }, [showFirms, configs, configuredFirmIds]);

  const hasEnabledFirm = useMemo(
    () => (configs ?? []).some((config) => config.enabled === true),
    [configs]
  );

  const isBusy = runningFirmId !== null || isBatchRunning;

  const handleRunImport = useCallback(
    async (
      firmId: string,
      options: { force?: boolean; feedData?: string; fileName?: string } = {}
    ) => {
      if (isBusy) {
        return;
      }
      setRunningFirmId(firmId);
      try {
        // The run nests its log: the response also carries whether the report was mailed.
        const log = (await runIntegratorImport(firmId, options))?.runLog;
        if (log) {
          setLatestByFirmId((prev) => {
            const next = new Map(prev);
            next.set(log.firmId, log);
            return next;
          });
        }
        // A failed run also answers 200: the outcome is in `status`, not in the response code.
        if (log?.status === "success") {
          // A run that reached the end succeeded, whatever each integrator managed to
          // write: what was inserted, updated or failed belongs in the report, not here.
          toastSuccess("Import completed.");
        } else if (log?.status === "skipped") {
          toastInfo("Feed unchanged since the last run — nothing new was stored.");
        } else {
          toastError(log?.errorMessage ?? "The run finished with an error.", {
            autoClose: 6000,
          });
        }
      } catch (e: any) {
        // Only what stopped the run from being recorded reaches here: 400 / 404 / 409.
        toastError(e?.message ?? "Could not run the import.", { autoClose: 6000 });
      } finally {
        setRunningFirmId(null);
      }
    },
    [isBusy]
  );

  const handleRunBatch = useCallback(async () => {
    if (isBusy) {
      return;
    }
    setIsBatchRunning(true);
    try {
      const result = await runIntegratorBatch();
      const jobs = result?.jobs ?? [];
      const failures = result?.failures ?? [];

      if (jobs.length > 0) {
        setLatestByFirmId((prev) => {
          const next = new Map(prev);
          for (const job of jobs) {
            next.set(job.firmId, job);
          }
          return next;
        });
        toastSuccess(
          jobs.length === 1
            ? "Batch queued for 1 firm."
            : `Batch queued for ${jobs.length} firms.`
        );
      } else {
        toastInfo("No enabled firms were queued.");
      }

      if (failures.length > 0) {
        toastError(
          failures.length === 1
            ? `1 firm could not be queued: ${failures[0].errorMessage}`
            : `${failures.length} firms could not be queued.`,
          { autoClose: 6000 }
        );
      }
    } catch (e: any) {
      toastError(e?.message ?? "Could not queue the batch.", { autoClose: 6000 });
    } finally {
      setIsBatchRunning(false);
    }
  }, [isBusy]);

  const columnsDef: IHeader[] = useMemo(
    () => [
      { id: "firmName", header: "Firm" },
      { id: "regionNames", header: "Region", defaultVisibility: false },
      { id: "membershipStatus", header: "Membership Status", defaultVisibility: false },
      { id: "integratorEnabled", header: "Enabled", enableSorting: false, width: 110 },
      {
        id: "executionCycles",
        header: "Execution Cycles",
        enableSorting: false,
        width: 160,
      },
      { id: "feedUrl", header: "URL", enableSorting: false, width: 280 },
      {
        id: "lastRunDate",
        header: "Last Run Date",
        enableSorting: false,
        width: 180,
        cell: ({ row }) => (
          <LastRunDateCell
            date={row.lastRunDate}
            status={row.latestRunLog?.status}
          />
        ),
      },
      { id: "lastVerified", header: "Last Verified", enableSorting: false, width: 150 },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        // Split Log control is h-8; keep the four actions on one line.
        width: 400,
        cell: ({ row }) => (
          <div className="flex flex-nowrap items-center justify-start gap-2 whitespace-nowrap">
            <DropdownButton
              color="neutral"
              style="outlined"
              action={{
                label: "Log",
                disabled: !row.latestRunLog,
                onClick: () => {
                  if (!row.latestRunLog) {
                    return;
                  }
                  navigate(`/integrator-engine/run-logs/${row.latestRunLog.id}`, {
                    state: row.latestRunLog,
                  });
                },
              }}
              items={[
                {
                  id: "all-logs",
                  label: "All Logs",
                  onSelect: () =>
                    navigate(`/integrator-engine/run-logs?firmId=${row.id}`),
                },
              ]}
            />
            <Button
              text="Configure"
              color="secondary"
              variant="filled"
              size="fixed"
              className={ACTION_BUTTON_CLASS}
              onClick={() =>
                navigate(`/integrator-engine/firms/${row.id}`, { state: row })
              }
            />
            <Button
              text="Professionals"
              color="neutral"
              variant="outlined"
              size="fixed"
              className={ACTION_BUTTON_CLASS}
              tooltip="Review the firm's professionals by segment"
              onClick={() => navigate(`/integrator-engine/professionals/${row.id}`)}
            />
            {/* Always rendered so every row keeps the same actions; only enabled when the firm
                has IntegratorFirm config. The endpoint still rejects disabled firms. */}
            <div
              title={runButtonTooltip(
                row.hasIntegratorConfig,
                runningFirmId,
                row.id,
                isBatchRunning
              )}
            >
              <DropdownButton
                color="accent"
                style="outlined"
                disabled={!row.hasIntegratorConfig || isBusy}
                action={{
                  label: "Run",
                  onClick: () => void handleRunImport(row.id),
                }}
                items={[
                  {
                    id: "force-run",
                    label: "Run (force)",
                    onSelect: () => void handleRunImport(row.id, { force: true }),
                  },
                  {
                    id: "upload",
                    label: "Upload",
                    onSelect: () => setUploadFirmId(row.id),
                  },
                ]}
              />
            </div>
          </div>
        ),
      },
    ],
    [navigate, runningFirmId, isBusy, isBatchRunning, handleRunImport]
  );

  const excelHeaders = useMemo(
    () =>
      columnsDef
        .filter((col) => !FIELDS_TO_IGNORE.has(col.id))
        .map((col) => getExcelColumnTitle(col)),
    [columnsDef]
  );

  const handleUploadConfirm = useCallback(
    async (payload: UploadFeedPayload) => {
      if (!uploadFirmId) {
        return;
      }
      await handleRunImport(uploadFirmId, payload);
      setUploadFirmId(null);
    },
    [uploadFirmId, handleRunImport]
  );

  const formatData = (rows: any[]) =>
    rows.map((row) => {
      const config = configByFirmId.get(row.id);
      const latestRunLog = latestByFirmId.get(row.id);
      return {
        ...row,
        name: firstNonBlankText(row.name, row.firmName),
        // The Firm column reads firmName. A blank legal name still has the unique name.
        firmName: firstNonBlankText(row.firmName, row.name),
        regionNames:
          formatRegionDisplay(row.regionNames ?? row.regionName) || NO_VALUE,
        integratorEnabled: config ? (config.enabled ? "Yes" : "No") : NO_VALUE,
        feedUrl: config?.feedUrl || NO_VALUE,
        executionCycles: NO_VALUE,
        lastRunDate: formatQueuedDate(latestRunLog?.queuedDate),
        lastVerified: NO_VALUE,
        hasIntegratorConfig: config != null,
        latestRunLog,
      };
    });

  const showFirmsOption = useMemo(
    () =>
      SHOW_FIRMS_OPTIONS.find((option) => option.id === showFirms) ??
      SHOW_FIRMS_OPTIONS[0],
    [showFirms]
  );

  const filtersData: IFilterData[] = useMemo(
    () => [
      {
        // No `fieldName`: the selection is translated into `persistentFilter`, because the
        // firm ids it filters by only exist after the configurations are loaded.
        name: SHOW_FIRMS_FILTER_NAME,
        type: "custom",
        defaultCustomFilterValue: SHOW_FIRMS_DEFAULT,
        getCustomSelectedChips: () =>
          showFirms === SHOW_FIRMS_DEFAULT
            ? []
            : [{ id: CUSTOM_FILTER_CHIP_ID, name: showFirmsOption.name }],
        render: ({ apply }) => (
          <DropDownMultiple<DropDownOptionBase>
            title="Show Firms:"
            options={SHOW_FIRMS_OPTIONS}
            value={showFirmsOption}
            onChange={(option) => {
              const next = option ? String(option.id) : SHOW_FIRMS_DEFAULT;
              setShowFirms(next);
              apply(next, null);
            }}
            searchable={false}
          />
        ),
      },
    ],
    [showFirms, showFirmsOption]
  );

  const handlePanelFilterReset = useCallback((resets: PanelFilterResetInfo[]) => {
    if (resets.some((reset) => reset.name === SHOW_FIRMS_FILTER_NAME)) {
      setShowFirms(SHOW_FIRMS_DEFAULT);
    }
  }, []);

  return (
    <div className="flex flex-col min-w-0">
      <div className="flex w-full items-center justify-end gap-4 rounded-t-lg bg-accent-Default-dark px-6 py-3">
        <Button
          text="Run Batch"
          color="white"
          variant="outlined"
          size="fixed"
          className="!w-auto shrink-0 whitespace-nowrap"
          disabled={isBusy || !hasEnabledFirm}
          tooltip={
            isBatchRunning
              ? "The batch is being queued…"
              : runningFirmId
                ? "A firm's import is already running"
                : hasEnabledFirm
                  ? "Queue an import for every enabled firm"
                  : "No enabled firms to queue"
          }
          icon={<Play />}
          onClick={() => void handleRunBatch()}
        />
        <Button
          text="All Logs"
          color="white"
          variant="outlined"
          size="fixed"
          className="!w-auto shrink-0 whitespace-nowrap"
          icon={<Log />}
          onClick={() => navigate("/integrator-engine/run-logs")}
        />
        <Button
          text="General Settings"
          color="white"
          variant="outlined"
          size="fixed"
          className="!w-auto shrink-0 whitespace-nowrap"
          icon={<Gear />}
          onClick={() => setSettingsOpen(true)}
        />
      </div>

      <GeneralSettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />

      <UploadFeedModal
        isOpen={uploadFirmId != null}
        onClose={() => setUploadFirmId(null)}
        onConfirm={handleUploadConfirm}
      />

      <FilteredTableCore
        columnsDef={columnsDef}
        showRowNumber
        endpoint={endpoint}
        filtersData={filtersData}
        formatData={formatData}
        excelHeaders={excelHeaders}
        excelFilename="integrator-firms"
        persistentFilter={persistentFilter}
        onPanelFilterReset={handlePanelFilterReset}
        // Show Firms lives in component state, so a saved API filter cannot be reflected back
        // into the dropdown: restoring one would show a list that contradicts what it reads.
        persistFilters={false}
        defaultSorting={{ field: "name", order: "asc" }}
        defaultPageSize={defaultPageSize}
        showSearchInput
        showSelectedFilters={false}
        inlineFilterToolbar
        searchPlaceholder="Search by firm name or Short ID"
        emptyStateMessage={
          configs === null ? "Loading firms…" : "No firms found"
        }
      />
    </div>
  );
}
